// src/pages/Users/Galleries/GalleriesPhotos.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getAllGalleries } from "../../../services/gallerieServices";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Button from "../../../components/Button/Button";
import { ToastContainer, toast } from "react-toastify";

import GalleryCard from "./GalleryCard";
import AlbumsToolbar from "./AlbumsToolbar";
import SkeletonAlbumCard from "./SkeletonAlbumCard";
import EmptyState from "./EmptyState";
import CreateAlbumModal from "./CreateAlbumModal";

const DEFAULT_UI = {
  q: "",
  sort: "recent", // recent | alpha | oldest
  showAll: false, // visible uniquement pour admin/enseignant
};

const collatorFR = new Intl.Collator("fr", { sensitivity: "base" });

export default function GalleriesPhotos() {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const isManager = userRole === "admin" || userRole === "enseignant";

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [ui, setUi] = useState(() => {
    const saved = localStorage.getItem("albums_ui_state");
    return saved ? { ...DEFAULT_UI, ...JSON.parse(saved) } : DEFAULT_UI;
  });
  const [debouncedQ, setDebouncedQ] = useState(ui.q);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const persistUi = (next) => {
    setUi(next);
    localStorage.setItem("albums_ui_state", JSON.stringify(next));
  };

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(() => persistUi({ ...ui, q: debouncedQ }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [debouncedQ]);

  const fetchGalleries = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await getAllGalleries();
      setGalleries(Array.isArray(resp) ? resp : []);
    } catch (e) {
      console.error("Failed to fetch galleries:", e);
      setError("Impossible de charger les galeries pour le moment.");
      toast.error("Erreur de chargement des galeries.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGalleries();
  }, [fetchGalleries]);

  const filtered = useMemo(() => {
    // Filtre actif/inactif selon rôle + showAll
    let base = galleries.filter((g) =>
      isManager ? (ui.showAll ? true : g.is_active) : g.is_active
    );

    // Recherche
    const q = ui.q.trim().toLowerCase();
    if (q) {
      base = base.filter((g) => {
        const t = g.titre?.toLowerCase() || "";
        const d = g.description?.toLowerCase() || "";
        return t.includes(q) || d.includes(q);
      });
    }

    // Tri
    base.sort((a, b) => {
      if (ui.sort === "alpha") {
        return collatorFR.compare(String(a.titre), String(b.titre));
      }
      const da =
        new Date(a.updated_at || a.created_at || 0).getTime() ||
        Number(a.id) ||
        0;
      const db =
        new Date(b.updated_at || b.created_at || 0).getTime() ||
        Number(b.id) ||
        0;
      if (ui.sort === "oldest")
        return da - db || collatorFR.compare(String(a.titre), String(b.titre));
      // recent (default)
      return db - da || collatorFR.compare(String(a.titre), String(b.titre));
    });

    return base;
  }, [galleries, ui, isManager]);

  const openCreate = () => setIsCreateModalOpen(true);
  const closeCreate = () => {
    setIsCreateModalOpen(false);
    fetchGalleries();
  };

  return (
    <section className="w-full">
      <ScrollIndicator />
      {/* Offset navbar */}
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        {/* Header page */}
        <HeaderTitle
          role="Utilisateur"
          title="Galeries Photos"
          align="center"
          className="mb-1"
        />

        {/* Navigation */}
        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        {/* Carte globale */}
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/80 dark:bg-slate-900/60 backdrop-blur mb-4">
          {/* Header gradient */}
          <div className="px-4 py-5 sm:px-6 bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold">
                  Albums photos du club
                </h2>
                <p className="text-white/90 text-sm">
                  Explore les souvenirs : événements, compétitions, stages…
                </p>
              </div>
              <div className="text-sm">
                <span className="font-semibold">{filtered.length}</span>{" "}
                album(s)
              </div>
            </div>
          </div>

          {/* Toolbar */}
          <div className="p-4 sm:p-6 border-b border-slate-200 dark:border-slate-800">
            <AlbumsToolbar
              ui={ui}
              onChange={(patch) => persistUi({ ...ui, ...patch })}
              debouncedQ={debouncedQ}
              setDebouncedQ={setDebouncedQ}
              isManager={isManager}
              onCreate={openCreate}
            />
          </div>

          {/* Contenu */}
          <div className="p-4 sm:p-6">
            {loading && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <SkeletonAlbumCard key={i} />
                ))}
              </div>
            )}

            {!loading && error && (
              <div className="text-center py-10 text-slate-600 dark:text-slate-300">
                {error}
              </div>
            )}

            {!loading && !error && filtered.length === 0 && (
              <EmptyState message="Aucun album ne correspond à votre recherche." />
            )}

            {!loading && !error && filtered.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((gallery, index) => (
                  <GalleryCard
                    key={gallery.id}
                    gallery={gallery}
                    index={index}
                    userRole={userRole}
                    refreshGalleriesList={fetchGalleries}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <ToastContainer />

      {/* Modale création (existante chez toi) */}
      {isCreateModalOpen && (
        <CreateAlbumModal
          isOpen={isCreateModalOpen}
          onClose={closeCreate}
          refreshList={fetchGalleries}
        />
      )}
    </section>
  );
}
